import { useEffect, useRef } from 'react';

export function ChangelogBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let time = 0;

    interface VersionBubble {
      x: number;
      y: number;
      vx: number;
      vy: number;
      version: string;
      size: number;
      opacity: number;
      color: string;
    }

    interface TimelineNode {
      x: number;
      y: number;
      progress: number;
      delay: number;
    }

    let versionBubbles: VersionBubble[] = [];
    let timelineNodes: TimelineNode[] = [];

    const versions = ['2.4', '2.3', '2.2', '2.1', '2.0', '1.5', '1.4', '1.3'];
    const colors = ['#a855f7', '#22d3ee', '#3b82f6', '#10b981', '#f59e0b'];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initElements();
    };

    const initElements = () => {
      versionBubbles = [];
      timelineNodes = [];

      // Create floating version bubbles
      for (let i = 0; i < 8; i++) {
        versionBubbles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3 - 0.2,
          version: versions[i % versions.length],
          size: 30 + Math.random() * 20,
          opacity: 0.1 + Math.random() * 0.15,
          color: colors[Math.floor(Math.random() * colors.length)],
        });
      }

      // Create timeline nodes
      const nodeCount = 8;
      for (let i = 0; i < nodeCount; i++) {
        timelineNodes.push({
          x: canvas.width * 0.1,
          y: (canvas.height / (nodeCount + 1)) * (i + 1),
          progress: 0,
          delay: i * 0.5,
        });
      }
    };

    const drawTimeline = () => {
      // Main timeline line
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(168, 85, 247, 0.1)';
      ctx.lineWidth = 2;
      ctx.moveTo(canvas.width * 0.1, 0);
      ctx.lineTo(canvas.width * 0.1, canvas.height);
      ctx.stroke();

      // Animated nodes on timeline
      timelineNodes.forEach((node, index) => {
        const animTime = Math.max(0, time - node.delay);
        const pulse = Math.sin(animTime * 2) * 0.5 + 0.5;

        // Node glow
        const gradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, 30);
        gradient.addColorStop(0, `rgba(168, 85, 247, ${0.3 * pulse})`);
        gradient.addColorStop(1, 'transparent');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, 30, 0, Math.PI * 2);
        ctx.fill();

        // Node circle
        ctx.beginPath();
        ctx.arc(node.x, node.y, 8 + pulse * 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(168, 85, 247, ${0.5 + pulse * 0.3})`;
        ctx.fill();

        // Connection line to right
        if (animTime > 0) {
          const lineProgress = Math.min(1, animTime * 0.5);
          ctx.beginPath();
          ctx.strokeStyle = `rgba(168, 85, 247, ${0.2 * lineProgress})`;
          ctx.lineWidth = 1;
          ctx.moveTo(node.x + 15, node.y);
          ctx.lineTo(node.x + 15 + (canvas.width * 0.3) * lineProgress, node.y);
          ctx.stroke();

          // Update arrow at the end
          if (lineProgress > 0.8) {
            const arrowX = node.x + 15 + (canvas.width * 0.3);
            ctx.beginPath();
            ctx.fillStyle = `rgba(168, 85, 247, ${0.3})`;
            ctx.moveTo(arrowX, node.y);
            ctx.lineTo(arrowX - 10, node.y - 5);
            ctx.lineTo(arrowX - 10, node.y + 5);
            ctx.closePath();
            ctx.fill();
          }
        }
      });
    };

    const drawVersionBubbles = () => {
      versionBubbles.forEach((bubble) => {
        bubble.x += bubble.vx;
        bubble.y += bubble.vy;

        // Wrap around
        if (bubble.y < -bubble.size) bubble.y = canvas.height + bubble.size;
        if (bubble.x < -bubble.size) bubble.x = canvas.width + bubble.size;
        if (bubble.x > canvas.width + bubble.size) bubble.x = -bubble.size;

        // Draw bubble
        ctx.beginPath();
        ctx.arc(bubble.x, bubble.y, bubble.size, 0, Math.PI * 2);
        ctx.strokeStyle = bubble.color.replace(')', `, ${bubble.opacity})`).replace('rgb', 'rgba').replace('#', 'rgba(').replace(/^rgba\(([a-f0-9]{2})([a-f0-9]{2})([a-f0-9]{2})/, (_, r, g, b) =>
          `rgba(${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}`
        );

        // Simplified color handling
        const alpha = bubble.opacity;
        if (bubble.color === '#a855f7') ctx.strokeStyle = `rgba(168, 85, 247, ${alpha})`;
        else if (bubble.color === '#22d3ee') ctx.strokeStyle = `rgba(34, 211, 238, ${alpha})`;
        else if (bubble.color === '#3b82f6') ctx.strokeStyle = `rgba(59, 130, 246, ${alpha})`;
        else if (bubble.color === '#10b981') ctx.strokeStyle = `rgba(16, 185, 129, ${alpha})`;
        else if (bubble.color === '#f59e0b') ctx.strokeStyle = `rgba(245, 158, 11, ${alpha})`;

        ctx.lineWidth = 2;
        ctx.stroke();

        // Version text
        ctx.font = `bold ${bubble.size * 0.4}px monospace`;
        ctx.fillStyle = ctx.strokeStyle;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`v${bubble.version}`, bubble.x, bubble.y);
      });
    };

    const drawUpdateArrows = () => {
      const arrowCount = 5;
      for (let i = 0; i < arrowCount; i++) {
        const x = canvas.width * 0.6 + (i * 80);
        const baseY = ((time * 50 + i * 200) % (canvas.height + 200)) - 100;
        const opacity = 0.1 + (Math.sin(time + i) * 0.05);

        ctx.save();
        ctx.translate(x, baseY);
        ctx.rotate(-Math.PI / 2);

        // Arrow shape
        ctx.beginPath();
        ctx.moveTo(0, -15);
        ctx.lineTo(10, 0);
        ctx.lineTo(5, 0);
        ctx.lineTo(5, 20);
        ctx.lineTo(-5, 20);
        ctx.lineTo(-5, 0);
        ctx.lineTo(-10, 0);
        ctx.closePath();

        ctx.fillStyle = `rgba(34, 211, 238, ${opacity})`;
        ctx.fill();

        ctx.restore();
      }
    };

    const drawProgressBars = () => {
      const barCount = 3;
      for (let i = 0; i < barCount; i++) {
        const x = canvas.width * 0.75;
        const y = canvas.height * 0.3 + i * 60;
        const width = 150;
        const height = 8;
        const progress = (Math.sin(time * 0.5 + i * 2) * 0.5 + 0.5);

        // Background
        ctx.fillStyle = 'rgba(168, 85, 247, 0.05)';
        ctx.fillRect(x, y, width, height);

        // Progress
        const gradient = ctx.createLinearGradient(x, y, x + width * progress, y);
        gradient.addColorStop(0, 'rgba(168, 85, 247, 0.2)');
        gradient.addColorStop(1, 'rgba(34, 211, 238, 0.2)');
        ctx.fillStyle = gradient;
        ctx.fillRect(x, y, width * progress, height);

        // Percentage text
        ctx.font = '10px monospace';
        ctx.fillStyle = 'rgba(168, 85, 247, 0.3)';
        ctx.fillText(`${Math.round(progress * 100)}%`, x + width + 10, y + 7);
      }
    };

    const drawCodeDiff = () => {
      const x = canvas.width * 0.8;
      const y = canvas.height * 0.6;
      const lineHeight = 16;

      ctx.font = '11px monospace';

      const lines = [
        { text: '- old code', color: 'rgba(239, 68, 68, 0.15)' },
        { text: '+ new feature', color: 'rgba(34, 197, 94, 0.15)' },
        { text: '+ improvements', color: 'rgba(34, 197, 94, 0.15)' },
      ];

      lines.forEach((line, i) => {
        const opacity = Math.sin(time * 2 + i * 0.5) * 0.5 + 0.5;
        ctx.fillStyle = line.color.replace('0.15', String(0.1 + opacity * 0.1));
        ctx.fillText(line.text, x, y + i * lineHeight);
      });
    };

    const animate = () => {
      time += 0.016;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Background gradient
      const gradient = ctx.createRadialGradient(
        canvas.width * 0.1,
        canvas.height * 0.5,
        0,
        canvas.width * 0.5,
        canvas.height * 0.5,
        canvas.width
      );
      gradient.addColorStop(0, 'rgba(168, 85, 247, 0.03)');
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      drawTimeline();
      drawVersionBubbles();
      drawUpdateArrows();
      drawProgressBars();
      drawCodeDiff();

      animationFrameId = requestAnimationFrame(animate);
    };

    resize();
    window.addEventListener('resize', resize);
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <canvas
        ref={canvasRef}
        className="w-full h-full opacity-70"
      />

      {/* Decorative elements */}
      <div className="absolute top-32 right-12 hidden lg:block">
        <div className="flex items-center gap-2 animate-pulse">
          <div className="w-3 h-3 rounded-full bg-green-500/30" />
          <span className="text-xs text-green-400/30 font-mono">NEW</span>
        </div>
      </div>

      <div className="absolute bottom-20 right-20 hidden lg:block">
        <div className="space-y-1">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="h-1 bg-gradient-to-r from-purple-500/20 to-transparent rounded animate-pulse"
              style={{
                width: `${60 - i * 12}px`,
                animationDelay: `${i * 0.2}s`
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
